import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';

const BlogCard = ({ blog }) => {
  const {
    id,
    title,
    excerpt,
    coverImage,
    videoUrl,
    category,
    publishedAt,
    readTime,
  } = blog;

  const formattedDate = publishedAt 
    ? format(new Date(publishedAt), 'MMM dd, yyyy')
    : 'Draft';

  return (
    <Link 
      to={`/blogs/${id}`}
      className="block bg-white rounded-[20px] overflow-hidden shadow-[0_8px_25px_rgba(0,0,0,0.1)] transition-all duration-300 hover:-translate-y-[5px] hover:shadow-[0_12px_35px_rgba(0,0,0,0.15)] flex flex-col h-full"
    >
      {/* Cover Media */}
      <div className="w-full h-[250px] bg-gradient-to-br from-[#e0e7ff] to-[#f3e8ff] relative overflow-hidden flex-shrink-0">
        {coverImage ? (
          <img 
            src={coverImage} 
            alt={title}
            className="absolute inset-0 w-full h-full object-contain"
          />
        ) : videoUrl ? (
          <>
            <video
              src={videoUrl}
              className="absolute inset-0 w-full h-full object-contain"
              preload="metadata"
              muted
              playsInline
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-[#667eea] ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#9ca3af] text-5xl">
            <span>📝</span>
          </div>
        )}
        {category && (
          <div className="absolute top-4 left-4">
            <span className="inline-block px-3 py-1 rounded-[15px] text-xs font-bold uppercase bg-[#d1fae5] text-[#065f46]">
              {category}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex-1 flex flex-col">
        {/* Title */}
        <h3 className="text-2xl font-bold text-[#1f2937] mb-3 leading-[1.3] line-clamp-2">
          {title}
        </h3>

        {/* Excerpt */}
        <p className="text-[#6b7280] leading-[1.6] mb-4 flex-1 line-clamp-3">
          {excerpt}
        </p>

        {/* Meta Information */}
        <div className="flex justify-between items-center pt-4 border-t border-[#e5e7eb] text-sm text-[#9ca3af]">
          <div className="flex items-center gap-1">
            <Calendar size={16} />
            <span>{formattedDate}</span>
          </div>

          {readTime && (
            <div className="flex items-center gap-1">
              <Clock size={16} />
              <span>{readTime} min read</span>
            </div>
          )}
        </div>

        {/* View Button */}
        <button className="w-full mt-4 bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white border-none py-3 px-6 rounded-[10px] font-semibold cursor-pointer transition-opacity hover:opacity-90">
          Read Full Article
        </button>
      </div>
    </Link>
  );
};

BlogCard.propTypes = {
  blog: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    excerpt: PropTypes.string.isRequired,
    content: PropTypes.string,
    coverImage: PropTypes.string,
    category: PropTypes.string,
    publishedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    readTime: PropTypes.number,
  }).isRequired,
};

export default BlogCard;
